/**
 * Merchant Integrations dashboard — iter 40b + Loop MWEB-2.
 * Reads /api/commerce/status and shows Shopify + WooCommerce + SDK + API keys
 * with test buttons per provider.
 *
 * Loop MWEB-2 · The "Install Web SDK" panel is now a first-class install
 * flow (status + steps + copy + test + docs link). It reuses the EXISTING
 * SDK at /sdk/v1/cyannect.js and the EXISTING ingest endpoint
 * POST /api/v1/track/sdk — no new event pipeline was introduced.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Store, Zap, Key, Copy, CheckCircle, AlertCircle, Check, Rocket,
  ExternalLink, BookOpen, Info, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function MerchantIntegrations() {
  const [status, setStatus] = useState(null);
  const [shopUrl, setShopUrl] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/commerce/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const startShopifyInstall = async () => {
    if (!shopUrl) return toast.error("Enter your .myshopify.com domain");
    setBusy(true);
    try {
      const { data } = await api.get(`/commerce/shopify/install?shop=${encodeURIComponent(shopUrl.trim())}`);
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Shopify not configured");
      setBusy(false);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  if (!status) return <div className="p-10 text-sm text-slate-500">Loading integrations…</div>;

  return (
    <div className="max-w-4xl mx-auto px-6 py-10 space-y-6" data-testid="merchant-integrations">
      <header>
        <div className="pf-chip pf-chip-cyan w-fit"><Zap className="w-3 h-3" /> Integrations</div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tight text-slate-900 mt-3">Connect your store</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Wire Cyannect into your existing checkout so every conversion, refund, and referral is reported automatically.
        </p>
      </header>

      {/* Shopify */}
      <section className="pf-card p-6" data-testid="integrations-shopify">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Store className="w-5 h-5 text-emerald-700" />
            </div>
            <div>
              <div className="font-display text-lg text-slate-900">Shopify</div>
              <div className="text-xs text-slate-500">OAuth · GraphQL Admin API 2026-07 · orders/create webhook</div>
            </div>
          </div>
          <StatusChip
            configured={status.shopify.configured}
            connected={status.shopify.connected}
            testid="shopify-status"
          />
        </div>
        {!status.shopify.configured && (
          <div className="mt-4 text-sm bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-900" data-testid="shopify-503-note">
            Shopify integration isn't live yet — the Cyannect team is finalizing the Partner app credentials. Check back soon.
          </div>
        )}
        {status.shopify.configured && !status.shopify.connected && (
          <div className="mt-4 flex gap-2 flex-wrap">
            <input
              value={shopUrl}
              onChange={(e) => setShopUrl(e.target.value)}
              placeholder="yourstore.myshopify.com"
              className="flex-1 min-w-[220px] pf-input text-sm px-3 py-2"
              data-testid="shopify-shop-input"
            />
            <button onClick={startShopifyInstall} disabled={busy}
                    className="pf-btn-primary px-4 py-2 rounded-full text-sm font-semibold disabled:opacity-60"
                    data-testid="shopify-install-btn">
              {busy ? "Redirecting…" : "Install Cyannect app"}
            </button>
          </div>
        )}
        {status.shopify.connected && (
          <div className="mt-4 text-sm text-slate-700">
            Connected to <span className="font-mono font-medium">{status.shopify.shop}</span> · installed {status.shopify.installed_at && new Date(status.shopify.installed_at).toLocaleDateString()}
          </div>
        )}
      </section>

      {/* WooCommerce */}
      <section className="pf-card p-6" data-testid="integrations-woocommerce">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
              <Store className="w-5 h-5 text-violet-700" />
            </div>
            <div>
              <div className="font-display text-lg text-slate-900">WooCommerce</div>
              <div className="text-xs text-slate-500">PHP plugin · woocommerce_new_order + payment_complete + refunded hooks</div>
            </div>
          </div>
        </div>
        <ol className="mt-4 text-sm text-slate-700 space-y-1 list-decimal list-inside">
          <li>Download the plugin below and upload it to <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">wp-content/plugins/cyannect/</code>.</li>
          <li>Activate it from <em>Plugins → Installed Plugins</em>.</li>
          <li>Paste your Bearer key at <em>WooCommerce → Cyannect</em>.</li>
        </ol>
        <a href="/sdk/v1/cyannect.php" download
           className="mt-4 inline-flex items-center gap-2 pf-btn-glass px-4 py-2 rounded-full text-sm font-semibold"
           data-testid="woo-download">
          Download cyannect.php
        </a>
      </section>

      {/* Website tracking SDK */}
      <WebSdkPanel storeId={status?.store_id} />

      {/* API keys shortcut */}
      <section className="pf-card p-6 flex items-center justify-between gap-4" data-testid="integrations-api-keys">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
            <Key className="w-5 h-5 text-slate-700" />
          </div>
          <div>
            <div className="font-display text-lg text-slate-900">API keys</div>
            <div className="text-xs text-slate-500">{status.api_keys_count} active key{status.api_keys_count === 1 ? "" : "s"} · Bearer or X-Api-Key</div>
          </div>
        </div>
        <a href="/merchant/api-keys" className="pf-btn-primary px-4 py-2 rounded-full text-sm font-semibold">Manage keys →</a>
      </section>
    </div>
  );
}

function StatusChip({ configured, connected, testid }) {
  if (!configured) return (
    <span className="pf-chip text-[10px] font-semibold bg-slate-100 text-slate-500" data-testid={`${testid}-not-configured`}>
      <AlertCircle className="w-3 h-3" /> Coming soon
    </span>
  );
  if (connected) return (
    <span className="pf-chip pf-chip-mint text-[10px] font-semibold" data-testid={`${testid}-connected`}>
      <CheckCircle className="w-3 h-3" /> Connected
    </span>
  );
  return (
    <span className="pf-chip pf-chip-cyan text-[10px] font-semibold" data-testid={`${testid}-available`}>
      Ready to install
    </span>
  );
}


/**
 * WebSdkPanel — Install Web SDK panel · Loop MWEB-2.
 *
 * Displays install status by polling GET /api/v1/merchant/sdk/status (which
 * reads the existing `sdk_events` collection populated by the existing
 * public ingest endpoint /api/v1/track/sdk). Never exposes secrets in the
 * browser snippet — only the public merchant store id.
 */
function WebSdkPanel({ storeId }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [copied, setCopied] = useState(false);

  async function load(showToast = false) {
    setRefreshing(true);
    try {
      const { data } = await api.get("/v1/merchant/sdk/status");
      setStatus(data);
      if (showToast) {
        if (data.fresh)          toast.success("SDK is firing events — you're connected.");
        else if (data.installed) toast.info("Older SDK events found — no traffic in the last 7 days.");
        else                     toast.info("No events yet — publish a page with the snippet and click Test again.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fetch SDK status.");
    } finally { setRefreshing(false); setLoading(false); }
  }

  useEffect(() => { load(false); }, []);

  const effectiveStoreId = storeId || status?.store_id || "YOUR_STORE_ID";
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const snippet =
`<script src="${origin}/sdk/v1/cyannect.js"></script>
<script>
  Cyannect.init({ merchantId: "${effectiveStoreId}" });
</script>`;

  function copySnippet() {
    navigator.clipboard.writeText(snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
    toast.success("SDK snippet copied.");
  }

  const installState = !status
    ? "unknown"
    : status.fresh
      ? "fresh"
      : status.installed
        ? "stale"
        : "not_installed";

  const badge = {
    fresh: (
      <span className="pf-chip pf-chip-mint text-[10px] font-semibold" data-testid="sdk-status-fresh">
        <CheckCircle className="w-3 h-3" /> Connected · {status?.events_7d || 0} events / 7d
      </span>
    ),
    stale: (
      <span className="pf-chip text-[10px] font-semibold bg-amber-100 text-amber-800" data-testid="sdk-status-stale">
        <AlertCircle className="w-3 h-3" /> Installed · no recent events
      </span>
    ),
    not_installed: (
      <span className="pf-chip pf-chip-cyan text-[10px] font-semibold" data-testid="sdk-status-not-installed">
        Ready to install
      </span>
    ),
    unknown: (
      <span className="pf-chip text-[10px] font-semibold bg-slate-100 text-slate-500" data-testid="sdk-status-unknown">
        …
      </span>
    ),
  }[installState];

  return (
    <section className="pf-card p-6" data-testid="integrations-sdk">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
            <Zap className="w-5 h-5 text-cyan-700" />
          </div>
          <div>
            <div className="font-display text-lg text-slate-900">Website Tracking SDK</div>
            <div className="text-xs text-slate-500">
              Preserves <code className="font-mono">cy_ref</code> attribution + reports non-financial funnel events.
              A mobile app is <b>not</b> required.
            </div>
          </div>
        </div>
        {badge}
      </div>

      {/* Store ID + docs link */}
      <div className="mt-4 grid md:grid-cols-2 gap-3">
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
            Your merchant ID
          </div>
          <div className="mt-1 flex items-center gap-2">
            <code className="font-mono text-xs text-slate-800 truncate" data-testid="sdk-merchant-id">
              {effectiveStoreId}
            </code>
            <button
              onClick={() => { navigator.clipboard.writeText(effectiveStoreId); toast.success("Merchant ID copied."); }}
              className="text-[11px] font-semibold text-cyan-700 hover:text-cyan-800 inline-flex items-center gap-1"
              data-testid="sdk-merchant-id-copy"
            >
              <Copy className="w-3 h-3" /> Copy
            </button>
          </div>
          <div className="text-[10px] text-slate-500 mt-1">Public — safe to expose in browser JavaScript.</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
            Full documentation
          </div>
          <Link
            to="/docs/web-sdk"
            className="mt-1 text-sm font-semibold text-cyan-700 hover:text-cyan-800 inline-flex items-center gap-1"
            data-testid="sdk-docs-link"
          >
            <BookOpen className="w-3.5 h-3.5" /> Read the Web SDK docs
            <ExternalLink className="w-3 h-3 opacity-60" />
          </Link>
          <div className="text-[10px] text-slate-500 mt-1">Install steps, event reference, security notes.</div>
        </div>
      </div>

      {/* Ordered install steps */}
      <ol className="mt-5 space-y-2 text-sm text-slate-700 list-decimal list-inside" data-testid="sdk-install-steps">
        <li>Copy the Cyannect script tag below.</li>
        <li>Paste it before <code className="font-mono text-xs bg-slate-100 px-1 rounded">&lt;/body&gt;</code> on every page of your website.</li>
        <li>Initialize with your <b>merchant ID</b> (already filled in for you).</li>
        <li>Click <em>Test integration</em> to confirm events are reaching Cyannect.</li>
      </ol>

      <pre className="mt-4 rounded-2xl bg-slate-900 text-slate-100 text-xs p-4 overflow-x-auto" data-testid="sdk-snippet">
{snippet}
      </pre>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          onClick={copySnippet}
          className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1"
          data-testid="sdk-copy"
        >
          {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
          {copied ? "Copied" : "Copy snippet"}
        </button>
        <button
          onClick={() => load(true)}
          disabled={refreshing || loading}
          className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50"
          data-testid="sdk-test-btn"
        >
          <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Checking…" : "Test integration"}
        </button>
        <Link
          to="/developers"
          className="text-xs font-semibold text-cyan-700 hover:text-cyan-800 underline underline-offset-2"
          data-testid="sdk-playground-link"
        >
          Try in the SDK playground →
        </Link>
      </div>

      {status?.last_event && (
        <div
          className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-xs text-emerald-900 flex items-start gap-2"
          data-testid="sdk-last-event"
        >
          <Rocket className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>
            Last event <code className="font-mono">{status.last_event.event}</code>
            {status.last_event.ts && (
              <> at {new Date(status.last_event.ts).toLocaleString()}</>
            )}
            {status.last_event.origin && (
              <> from <span className="font-mono truncate">{status.last_event.origin}</span></>
            )}.
          </div>
        </div>
      )}

      <div className="mt-4 text-[11px] text-slate-500 flex items-start gap-1.5">
        <Info className="w-3 h-3 mt-0.5 shrink-0" />
        Never place API secrets or bearer keys in browser JavaScript.
        Verified conversions are reported server-to-server via
        <code className="font-mono text-[11px] bg-slate-100 px-1 rounded ml-1">POST /api/v1/conversions</code>.
      </div>
    </section>
  );
}
